import type { ReactElement } from 'react';

import { motionAllowed } from './motion';

/* The Lumiq loader: the official three-second logo animation, drawn from one sprite sheet.
 *
 * **CSS only — no video element and no per-frame JavaScript.** The sprite is the brand's loader
 * video keyed to transparency: 48 frames at 16 fps in an 8×6 grid. Two stepped animations walk it,
 * one across a row every half second and one down the rows every three seconds, so a frame is a
 * `background-position` and the browser does all of the work. A `<video>` would mean a decoder per
 * instance and an autoplay policy to argue with; this is one image, cached once, at any size.
 *
 * **For long work only.** A harvest, a bulk profile, the app starting up — operations measured in
 * seconds or minutes, where the brand earns its place. A button that is busy for 300 ms keeps the
 * small spinner, and a page that is loading keeps its skeleton: a logo animation on every fetch
 * would turn the product's mark into noise.
 *
 * **`once` plays the drawing and holds on the finished logo.** Sign-in uses it: frames 0–36 once,
 * then the completed lockup, so the first screen ends on the brand rather than looping at somebody
 * typing a password.
 *
 * **Reduced motion gets the still logo**, decided twice: here, from `motionAllowed()`, and in the
 * stylesheet, so the preference still wins if it changes while the page is open.
 *
 * **On a dark surface the loader sits on a white plate.** The source frames were keyed on light
 * surfaces, so the black "iQ" and "DATA" vanish on a dark one; the plate keeps the whole mark
 * legible until the brand supplies a white-on-dark variant.
 */

export type BrandLoaderSize = 'sm' | 'md' | 'lg';

export interface BrandLoaderProps {
  /** `sm` 41×24 beside a line of text, `md` 48×28 in a card, `lg` 220×128 on its own. */
  readonly size?: BrandLoaderSize;
  /** Play the drawing once and hold on the finished logo (sign-in). */
  readonly once?: boolean;
  /** An accessible name. Without one the loader is decorative, because the text beside it — "Harvesting",
   * "Profiling" — already says what is happening. */
  readonly label?: string;
  readonly className?: string;
}

export function BrandLoader({
  size = 'md',
  once = false,
  label,
  className,
}: BrandLoaderProps): ReactElement {
  const classes = [
    'lq-brandloader',
    once ? 'lq-brandloader--once' : `lq-brandloader--${size}`,
    motionAllowed() ? null : 'lq-brandloader--still',
    className,
  ]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ');

  return label === undefined ? (
    <span className={classes} aria-hidden="true" data-lq-sprite="" />
  ) : (
    <span className={classes} role="img" aria-label={label} data-lq-sprite="" />
  );
}
